import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EventEmitter,
    Input,
    model,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
} from '@angular/core';
import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { FormControl, FormsModule } from '@angular/forms';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatOption,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

import { SelectOption, SelectOptionWithGroup } from '@models/shared.model';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';

/**
 * @title Chips Autocomplete
 */
@Component({
    selector: 'app-chips-autocomplete',
    templateUrl: 'chips-autocomplete.component.html',
    styleUrl: 'chips-autocomplete.component.scss',
    imports: [
        MatFormFieldModule,
        MatChipsModule,
        MatIconModule,
        TooltipComponent,
        MatAutocompleteModule,
        FormsModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsAutocompleteComponent implements OnInit, OnChanges {
    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() options: SelectOption[] | SelectOptionWithGroup[] = [];
    @Input() disabledOptions: (number | string)[] = [];
    @Input() inputControl = new FormControl<SelectOption[]>([]);
    /**
     * Programmatically override the current selection from outside the component.
     * Setting this to a non-null array replaces the current chips selection.
     * Useful when parent logic (e.g., automation rules) needs to update the
     * displayed chips without going through the FormControl directly, since the
     * component manages its own internal `selectedOptions` signal.
     */
    @Input() externalSelection: SelectOption[] | null = null;
    @Output() invalidityChange = new EventEmitter();

    isGroupedOptions: boolean = false;
    readonly separatorKeysCodes: number[] = [ENTER, COMMA, SEMICOLON];
    readonly current = model<any>(null);
    readonly selectedOptions = signal<(SelectOption & { invalid?: boolean })[]>(
        [],
    );

    readonly filteredOptions = computed(() => {
        if (this.current()?.viewValue) {
            return this.flatOptions.slice();
        } else {
            const current = this.current();

            return current
                ? this.filter(this.flatOptions, current)
                : this.flatOptions.slice();
        }
    });

    readonly filteredGroupOptions = computed(() => {
        if (this.current()?.viewValue) {
            return this.groupedOptions.slice();
        } else {
            const current = this.current();

            return current
                ? this.filterGroup(current)
                : this.groupedOptions.slice();
        }
    });

    get groupedOptions(): SelectOptionWithGroup[] {
        return this.options.filter(
            (opt): opt is SelectOptionWithGroup => 'items' in opt,
        );
    }

    get flatOptions(): SelectOption[] {
        return this.options.filter(
            (opt): opt is SelectOption => !('items' in opt),
        );
    }

    get invalid() {
        return this.selectedOptions().some((option) => option.invalid);
    }

    get noResult(): boolean {
        return this.getMatchedItemCount() === 0;
    }

    constructor() {
        effect(() => {
            this.inputControl.setValue(this.selectedOptions(), {
                emitEvent: false,
            });
        });

        effect(() => {
            this.invalidityChange.emit(this.invalid);
        });
    }

    ngOnInit() {
        this.selectedOptions.update(() => [
            ...(this.inputControl.value as SelectOption[]),
        ]);

        this.isGroupedOptions =
            this.options.length > 0 && 'items' in this.options[0];
    }

    ngOnChanges(changes: SimpleChanges): void {
        const disabledOptionsChange = changes['disabledOptions'];

        if (disabledOptionsChange) {
            this.removeDisabledOptions();
        }

        const externalSelectionChange = changes['externalSelection'];
        if (externalSelectionChange && this.externalSelection !== null) {
            this.selectedOptions.set([...this.externalSelection]);
        }
    }

    add(event: MatChipInputEvent): void {
        this.addValue(event.value);
    }

    remove(option: SelectOption): void {
        this.selectedOptions.update((options) => {
            const index = options.indexOf(option);
            if (index < 0) {
                return options;
            }

            options.splice(index, 1);
            return [...options];
        });
    }

    removeDisabledOptions() {
        const disabledOptionsSet = new Set(this.disabledOptions);
        this.selectedOptions.update((options) => {
            const filteredOptions = options.filter(
                (opt) => !disabledOptionsSet.has(opt.value),
            );

            return [...filteredOptions];
        });
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        const option = event.option || '';

        if (!this.isOptionSelected(option)) {
            this.selectedOptions.update((options) => [
                ...options,
                event.option.value,
            ]);

            this.clearCurrent();
            event.option.deselect();
        }
    }

    isOptionDisabled(value: string | number) {
        return this.disabledOptions.some(
            (disabledOption) => disabledOption === value,
        );
    }

    private addValue(value: string) {
        const trimmedValue = (value || '').trim();

        if (trimmedValue) {
            const matchedItems = this.getMatchedItemCount();

            if (matchedItems === 1) {
                const matchedItem = this.filteredGroupOptions().find(
                    (obj) => obj.items.length === 1,
                )!.items[0];
                const itemAdded = this.selectedOptions().some(
                    (i) => i.value === matchedItem.value,
                );

                if (!itemAdded) {
                    this.selectedOptions.update((options) => [
                        ...options,
                        matchedItem,
                    ]);
                }
                this.clearCurrent();
            }

            if (matchedItems === 0) {
                this.selectedOptions.update((options) => [
                    ...options,
                    {
                        value,
                        viewValue: value,
                        invalid: true,
                    },
                ]);
                this.clearCurrent();
            }
        }
    }

    private filter(options: SelectOption[], name: string): SelectOption[] {
        const filterValue = name.toLowerCase();
        const results = options.filter((option) =>
            option.viewValue.toLowerCase().includes(filterValue),
        );

        return results;
    }

    private filterGroup(value: string): SelectOptionWithGroup[] {
        if (value) {
            return this.groupedOptions.map((opt) => ({
                group: opt.group,
                items: this.filter(opt.items, value),
            }));
        }

        return this.groupedOptions;
    }

    private isOptionSelected(option: MatOption) {
        return this.selectedOptions().some(
            (i) => i.value === option.value.value,
        );
    }

    private getMatchedItemCount(): number {
        return this.filteredGroupOptions().reduce(
            (sum, obj) => sum + (obj.items ? obj.items.length : 0),
            0,
        );
    }

    private clearCurrent() {
        this.current.set('');
    }
}
